import type { MutableState } from 'src/replicant/State';
import * as Level from '.';
import * as Inventory from '../inventory';
import { Milestone, MilestoneEvolution } from './level.ruleset';
import type { CurrencyReplicantAPI } from '../inventory/inventory.utils';
import { AnalyticsEvent } from '../analytics';

export function incrementXp(
  state: MutableState,
  milestone: Milestone,
  petStage: MilestoneEvolution,
): AnalyticsEvent[] {
  const { milestoneXp } = Level.ruleset;

  // Grant XP.
  const xpGrant = milestoneXp(milestone, petStage);
  state.level.xp += xpGrant;

  // Create tracking event.
  const events: AnalyticsEvent[] = [
    {
      eventType: 'XPGrant',
      eventProperties: {
        xp: xpGrant,
        feature: milestone,
        $subFeature: milestone === 'debut' ? 'n/a' : petStage,
      },
    },
  ];

  // Check if level up.
  events.push(...levelUp(state));

  return events;
}

export function levelUp(state: MutableState): {
  eventType: 'PlayerLevelUp';
  eventProperties: { oldLevel: number; newLevel: number };
}[] {
  const { requiredXp, isMaxLevel } = Level.ruleset;
  let levelDiff = 0;

  // Player reached max level. No level up.
  if (isMaxLevel(state.level.current)) return [];

  // Level up as many levels as possible.
  while (state.level.xp >= requiredXp(state.level.current)!) {
    // Decrease XP by the amount of XP required to level up.
    state.level.xp -= requiredXp(state.level.current)!;

    // Level up.
    state.level.current++;
    levelDiff++;
  }

  const events: {
    eventType: 'PlayerLevelUp';
    eventProperties: {
      oldLevel: number;
      newLevel: number;
    };
  }[] = [];

  // If level up, create event.
  if (levelDiff > 0) {
    events.push({
      eventType: 'PlayerLevelUp',
      eventProperties: {
        oldLevel: state.level.current - levelDiff + 1,
        newLevel: state.level.current + 1,
      },
    });
  }

  return events;
}

export function claimLevelRewards(
  state: MutableState,
  api: CurrencyReplicantAPI,
): void {
  const { current, lastLevelReward } = state.level;
  const levelDiff = current - lastLevelReward;

  // No rewards to claim.
  if (levelDiff <= 0) return;

  // Award level up reward.
  Inventory.modifiers.addPoints(
    state,
    Level.ruleset.levelUpReward(lastLevelReward, current),
    {
      feature: 'player_level_up',
    },
    api,
  );

  // Set last level reward to current level.
  state.level.lastLevelReward = current;
}
